import { useState, useCallback } from 'react';
import { LoggerFactory } from '@cvent/logging/LoggerFactory';

const LOG = LoggerFactory.create('useLocalStorage');

/**
 * Hook that reads/writes from local storage
 * Do not use with sensitive/private data as this does not get cleared on logout.
 */
const useLocalStorage = <T = unknown>(
  key: string,
  initialValue: T
): [T, (valueToWrite: T | ((prevState: T) => T)) => void] => {
  const [savedValue, setSavedValue] = useState(() => {
    try {
      // if the value is a JSON object, we'll want to parse it
      // Check if window is defined for avoiding any error/warning when the page is server side rendered
      const storedValue = typeof window !== 'undefined' ? localStorage.getItem(key) : null;
      return storedValue ? JSON.parse(storedValue) : initialValue;
    } catch (parseException) {
      // not JSON, so we can just return the initial value
      LOG.warn('Unable to parse local stored value', parseException);
      return initialValue;
    }
  });

  const setValue = useCallback(
    valueToWrite => {
      try {
        if (typeof valueToWrite === 'function') {
          setSavedValue(current => {
            const newValue = valueToWrite(current);
            localStorage.setItem(key, JSON.stringify(newValue));
            return newValue;
          });
        } else {
          setSavedValue(valueToWrite);
          localStorage.setItem(key, JSON.stringify(valueToWrite));
        }
      } catch (error) {
        LOG.error('Unable to write item to local storage', error);
      }
    },
    [key]
  );

  return [savedValue, setValue];
};

export default useLocalStorage;
